import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { TextField, Box, Typography } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import MaskedInput from 'react-text-mask';
import { UtilsLib } from '@payface-frontends-libs/utils';
import { isValidCPF } from '@brazilian-utils/brazilian-utils';
import { login } from './services/authJwt';
const { environmentConfig } = UtilsLib.Config;
const { messages } = UtilsLib.Error;
export function LoginForm({ loginEndpoint, handleVerify }) {
    const [recaptcha, setRecaptcha] = useState('');
    const [cpfNumber, setCpfNumber] = useState('');
    const [verifyPage, setVerifyPage] = useState('');
    const [authJWTState, setAuthJWTState] = useState({
        errorLogin: '',
        isRequesting: false,
        exists: false,
    });
    const { enqueueSnackbar } = useSnackbar();
    const cpfCorrectLength = 11;
    const LoginSchema = Yup.object().shape({
        cpf: Yup.string()
            .required('CPF e reCAPTCHA são obrigatórios')
            .test('valid-cpf', 'Número de CPF inválido', (value) => {
            const minLengthAlert = 10;
            if (cpfNumber.length >= minLengthAlert) {
                return isValidCPF(value);
            }
            return true;
        }),
    });
    async function handleLogin({ cpf, recaptcha }) {
        try {
            setAuthJWTState({ ...authJWTState, isRequesting: true, errorLogin: '' });
            const response = await login({ cpf, recaptcha, loginEndpoint });
            response.enablePasswordUsage === false ? setVerifyPage('otp') : setVerifyPage('password');
            setAuthJWTState({
                ...authJWTState,
                isRequesting: false,
                exists: response.exists,
                errorLogin: '',
            });
        }
        catch (error) {
            setAuthJWTState({ ...authJWTState, isRequesting: false, exists: false, errorLogin: error.message });
        }
    }
    const formik = useFormik({
        initialValues: {
            cpf: '',
        },
        validationSchema: LoginSchema,
        onSubmit: async (_values, { resetForm }) => {
            try {
                await handleLogin({
                    cpf: cpfNumber,
                    recaptcha,
                });
            }
            catch (error) {
                resetForm({
                    values: {
                        cpf: '',
                    },
                });
            }
        },
    });
    const { errors, handleSubmit, getFieldProps } = formik;
    const renderErrorMessage = () => {
        const errorKeys = Object.keys(messages);
        const errorValues = Object.values(messages);
        const indexOfErrorMessage = errorKeys.findIndex((error) => error === authJWTState.errorLogin);
        enqueueSnackbar(errorValues[indexOfErrorMessage], { variant: 'error' });
    };
    useEffect(() => {
        const { errorLogin } = authJWTState;
        if (authJWTState.exists && !errorLogin) {
            handleVerify(verifyPage);
        }
    }, [authJWTState.exists]);
    useEffect(() => {
        if (authJWTState.errorLogin) {
            renderErrorMessage();
        }
    }, [authJWTState]);
    useEffect(() => {
        const cpf = getFieldProps('cpf').value;
        const regexExtractStringNumber = /[^\w\s]/gi;
        const regexRemoveUnderscore = /_/g;
        const matchedCpfNumbers = cpf.replace(regexExtractStringNumber, '');
        const matchedCpfNumbersWithoutUnderscore = matchedCpfNumbers.replace(regexRemoveUnderscore, '');
        setCpfNumber(matchedCpfNumbersWithoutUnderscore);
    }, [getFieldProps('cpf')]);
    return (_jsxs(_Fragment, { children: [_jsx(Box, Object.assign({ sx: { mt: 2, mb: 4 } }, { children: _jsx(Typography, Object.assign({ sx: { color: 'text.secondary' } }, { children: "Insira seus dados abaixo." }), void 0) }), void 0), _jsx(FormikProvider, Object.assign({ value: formik }, { children: _jsxs(Form, Object.assign({ autoComplete: "off", noValidate: true, onSubmit: handleSubmit }, { children: [_jsx(MaskedInput, Object.assign({}, getFieldProps('cpf'), { mask: [/[0-9]/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/], placeholder: "___.___.___-__", id: "cpf-input", render: (ref, props) => (_jsx(TextField, Object.assign({}, props, { inputRef: ref, fullWidth: true, label: "Insira seu CPF", error: Boolean(errors.cpf), helperText: _jsx("span", { children: errors.cpf }, void 0), sx: { mb: 3 } }), void 0)) }), void 0), _jsx(Box, Object.assign({ sx: {
                                mt: 0,
                                mb: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            } }, { children: _jsx(ReCAPTCHA, { sitekey: environmentConfig.recaptcha || '', onChange: (value) => setRecaptcha(value || '') }, void 0) }), void 0), _jsx(LoadingButton, Object.assign({ name: "login", fullWidth: true, disabled: cpfNumber.length !== cpfCorrectLength || recaptcha === '' || isValidCPF(cpfNumber) === false, size: "large", type: "submit", variant: "contained", pending: authJWTState.isRequesting }, { children: "Login" }), void 0)] }), void 0) }), void 0)] }, void 0));
}
//# sourceMappingURL=LoginForm.js.map